import styled from '@emotion/styled';
import { Text, Tooltip } from '@innovationdepartment/proxima-ui';
import type React from 'react';
import type { ReactNode } from 'react';

const Container = styled.div`
  font-family: 'Inter', sans-serif;
  background-color: #f8fafc;
  color: #1e293b;
  margin: 0;
  padding: 2.5rem;
  -webkit-font-smoothing: antialiased;
`;

const PersonasOverviewSection = styled.div`
  max-width: 1400px;
  margin: 0 auto 2.5rem auto;
  background: white;
  border-radius: 12px;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.07),
    0 2px 4px -2px rgb(0 0 0 / 0.07);
  border: 1px solid #e2e8f0;
`;

const PersonasContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    max-width: 700px;
  }
`;

const PersonaCard = styled.article`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.07),
    0 2px 4px -2px rgb(0 0 0 / 0.07);
  border: 1px solid #e2e8f0;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  height: fit-content;

  > section + section {
    border-top: 1px solid #e2e8f0;
    padding-top: 2rem;
    margin-top: 2rem;
  }

  > section:nth-of-type(3) {
    padding-bottom: 1rem;
  }

  > section:nth-of-type(3) + section {
    padding-top: 1rem;
    margin-top: 1rem;
  }

  > section:nth-of-type(4) + section {
    padding-top: 1.5rem;
    margin-top: 1.5rem;
  }
`;

const CardHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.5rem;
    margin: 0;
    font-weight: 700;
    line-height: 1.2;
  }

  .percentage {
    background-color: #eef2ff;
    color: #4338ca;
    padding: 0.5rem 1rem;
    border-radius: 99px;
    font-size: 1rem;
    font-weight: 600;
    flex-shrink: 0;
  }
`;

const PersonaDescription = styled.section`
  margin-top: 1.5rem;
  margin-bottom: 0;
  min-height: 3rem;
  display: flex;
  align-items: flex-start;

  p {
    margin: 0;
    line-height: 1.5rem;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    color: #64748b;
    font-size: 1rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: #1e293b;
`;

const FinancialGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FinancialItem = styled.div`
  .title {
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  .values-container {
    display: flex;
    gap: 1.5rem;
  }

  .value-block .value {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
  }

  .value-block .label {
    font-size: 0.8rem;
    color: #64748b;
    font-weight: 500;
  }
`;

const InsightTag = styled.span<{ variant: 'positive' | 'neutral' | 'negative' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 1rem;
  padding: 0.2rem 0.6rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.variant) {
      case 'positive':
        return '#f0fdf4';
      case 'negative':
        return '#fef2f2';
      default:
        return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'positive':
        return '#166534';
      case 'negative':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  }};
  border: 1px solid
    ${props => {
      switch (props.variant) {
        case 'positive':
          return '#bbf7d0';
        case 'negative':
          return '#fecaca';
        default:
          return '#d1d5db';
      }
    }};
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-grow: 1;
  min-height: 8rem;

  li {
    font-size: 0.875rem;
    color: #64748b;
    padding: 0.5rem 0;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    &:last-child {
      border-bottom: none;
    }

    span:last-child {
      font-weight: 600;
      color: #1e293b;
    }
  }
`;

const ProductCategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }

  > div {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

const SectionTitleWithInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  margin-bottom: 1.25rem;

  h4 {
    margin: 0;
    font-size: 0.9rem;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }

  > div {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

const DetailsList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-grow: 1;
  min-height: 10rem;

  li {
    display: grid;
    grid-template-columns: 60px 1fr 40px;
    gap: 0.75rem;
    align-items: center;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .bar-container {
    background-color: #f1f5f9;
    border-radius: 4px;
    height: 8px;
    width: 100%;
    overflow: hidden;
  }

  .bar {
    background-color: #4338ca;
    height: 100%;
    border-radius: 4px;
  }

  .label {
    color: #1e293b;
    white-space: nowrap;
  }

  .value {
    color: #64748b;
    text-align: right;
    font-weight: 600;
    font-size: 0.8rem;
  }
`;

const GeoDistributionSummary = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  margin-top: -0.5rem;
  margin-bottom: 1.5rem;
`;

const GeoCommunityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const GeoCommunityItem = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  .icon {
    flex-shrink: 0;
    margin-top: 2px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .info h4 {
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    font-weight: 600;
  }

  .info p {
    margin: 0;
    font-size: 0.875rem;
    color: #64748b;
    line-height: 1.5;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 0.5rem;
    margin-top: 0.25rem;
  }
`;

const LocationTag = styled.span`
  display: inline-flex;
  align-items: center;

  &::after {
    content: '•';
    color: #cbd5e1;
    margin-left: 0.5rem;
  }

  &:last-child::after {
    content: '';
  }
`;

const OverviewTable = styled.div`
  overflow: hidden;
`;

const OverviewTableHeader = styled.div`
  background: #ffffff;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 12px 12px 0 0;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
  }

  .subtitle {
    margin: 0.5rem 0 0 0;
    font-size: 0.875rem;
    color: #64748b;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    background: #ffffff;
    padding: 1rem 1.5rem;
    text-align: left;
    font-weight: 600;
    font-size: 0.875rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #e2e8f0;

    &:first-child {
      width: 35%;
    }
  }

  td {
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  tbody tr:hover {
    background: #f8fafc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  @media (max-width: 640px) {
    th,
    td {
      padding: 0.75rem 1rem;
      font-size: 0.8rem;
    }

    th:first-child {
      width: 40%;
    }
  }
`;

const PersonaName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const OverviewPercentage = styled.span`
  font-weight: 700;
  font-size: 1.125rem;
  color: #1e293b;
`;

const OverviewMetric = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: #1e293b;
`;

const OverviewProduct = styled.span`
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.4;
`;

interface LtvDistribution {
  '50th_percentile': number;
  '90th_percentile': number;
  '50th_percentile_vs_average': string;
}

interface IncomeDistribution {
  '50th_percentile': number;
  '90th_percentile': number;
  '90th_percentile_vs_average': string;
}

interface BrandFirstProductPurchased {
  product: string;
  percentage: number;
}

interface OtherBrandProductCategory {
  category: string;
  percentage: number;
}

interface SpecializedIndividualTag {
  display_name: string;
  percentage_population: number;
}

interface SpecializedTagType {
  type_display_name: string;
  type_helper_text: string;
  individual_tags?: SpecializedIndividualTag[];
}

interface Persona {
  persona_name: string;
  persona_description: string;
  percentage_of_audience: number;
  first_purchase_date_range?: {
    start_date: string;
    end_date: string;
  };
  ltv_distribution: LtvDistribution;
  income_distribution: IncomeDistribution;
  top_brand_first_products_purchased?: BrandFirstProductPurchased[];
  top_other_brands_product_categories?: OtherBrandProductCategory[];
  specialized_tag_types?: SpecializedTagType[];
  age_distribution: Record<string, number>;
  gender_distribution: Record<string, number>;
  residence_type_breakdown: Record<'SUBURBAN' | 'METRO_SPRAWL' | 'URBAN' | 'RURAL', number>;
  settlement_tag_breakdown?: Record<string, string[]>;
}

interface PersonaCoverage {
  name: string;
  percentage_coverage: number;
}

interface BrandPersonasData {
  personasData: {
    personas: Persona[];
    analysis_metadata?: {
      all_personas?: PersonaCoverage[];
    };
  };
}

interface BrandPersonasViewProps {
  getLtvTagClass: (text: string) => 'positive' | 'neutral' | 'negative';
  formatSettlementTitle: (key: string) => string;
  getSettlementIcon: (key: string) => ReactNode;
  formatDateRange: (start: string, end: string) => string;
  brandPersonasData: BrandPersonasData;
}

const BrandPersonasView: React.FC<BrandPersonasViewProps> = ({
  brandPersonasData,
  getLtvTagClass,
  formatSettlementTitle,
  getSettlementIcon,
  formatDateRange
}) => {
  const { personas, analysis_metadata: analysisMetadata } = brandPersonasData.personasData;

  return (
    <Container>
      {/* Personas Overview Section */}
      <PersonasOverviewSection>
        <OverviewTable>
          <OverviewTableHeader>
            <h2>Personas Overview</h2>
            <p className="subtitle">
              First purchase between{' '}
              {!!personas?.[0]?.first_purchase_date_range &&
                formatDateRange(
                  personas[0].first_purchase_date_range.start_date,
                  personas[0].first_purchase_date_range.end_date
                )}
            </p>
          </OverviewTableHeader>
          <Table>
            <thead>
              <tr>
                <th>Persona</th>
                <th>Coverage</th>
                <th>LTV</th>
                <th>Income</th>
                <th>Most Common First Purchase</th>
              </tr>
            </thead>
            <tbody>
              {analysisMetadata?.all_personas
                ?.sort((a, b) => b.percentage_coverage - a.percentage_coverage)
                .map(metaPersona => {
                  const detailedPersona = personas.find(p => p.persona_name === metaPersona.name);
                  return (
                    <tr key={`${metaPersona.name}`}>
                      <td>
                        <PersonaName>
                          <span>{metaPersona.name}</span>
                        </PersonaName>
                      </td>
                      <td>
                        <OverviewPercentage>{metaPersona.percentage_coverage}%</OverviewPercentage>
                      </td>
                      <td>
                        <OverviewMetric>
                          {detailedPersona?.ltv_distribution
                            ? `$${detailedPersona.ltv_distribution['50th_percentile'].toFixed(2)}`
                            : '--'}
                        </OverviewMetric>
                      </td>
                      <td>
                        <OverviewMetric>
                          {detailedPersona?.income_distribution
                            ? `$${Math.round(
                                detailedPersona.income_distribution['50th_percentile'] / 1000
                              )}k`
                            : '--'}
                        </OverviewMetric>
                      </td>
                      <td>
                        <OverviewProduct>
                          {detailedPersona?.top_brand_first_products_purchased?.[0]?.product ||
                            '--'}
                        </OverviewProduct>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </OverviewTable>
      </PersonasOverviewSection>

      {/* Personas Cards */}
      <PersonasContainer>
        {personas?.map(persona => (
          <PersonaCard key={`persona-${persona.persona_name}`}>
            <CardHeader>
              <h2>{persona.persona_name}</h2>
              <span className="percentage">{persona.percentage_of_audience}%</span>
            </CardHeader>

            <PersonaDescription>
              <p>{persona.persona_description}</p>
            </PersonaDescription>

            {/* Financials */}
            <section>
              <FinancialGrid>
                <FinancialItem>
                  <h3 className="title">Lifetime Value</h3>
                  <div className="values-container">
                    <div className="value-block">
                      <p className="value">
                        ${persona.ltv_distribution['50th_percentile'].toFixed(2)}
                      </p>
                      <p className="label">50th Percentile</p>
                    </div>
                    <div className="value-block">
                      <p className="value">
                        ${persona.ltv_distribution['90th_percentile'].toFixed(2)}
                      </p>
                      <p className="label">90th Percentile</p>
                    </div>
                  </div>
                  <InsightTag
                    variant={getLtvTagClass(persona.ltv_distribution['50th_percentile_vs_average'])}
                  >
                    {persona.ltv_distribution['50th_percentile_vs_average']}
                  </InsightTag>
                </FinancialItem>

                <FinancialItem>
                  <h3 className="title">Income</h3>
                  <div className="values-container">
                    <div className="value-block">
                      <p className="value">
                        ${persona.income_distribution['50th_percentile'].toLocaleString('en-US')}
                      </p>
                      <p className="label">50th Percentile</p>
                    </div>
                    <div className="value-block">
                      <p className="value">
                        ${persona.income_distribution['90th_percentile'].toLocaleString('en-US')}
                      </p>
                      <p className="label">90th Percentile</p>
                    </div>
                  </div>
                  <InsightTag
                    variant={
                      persona.income_distribution['90th_percentile_vs_average'].includes('↑')
                        ? 'positive'
                        : 'neutral'
                    }
                  >
                    {persona.income_distribution['90th_percentile_vs_average']}
                  </InsightTag>
                </FinancialItem>
              </FinancialGrid>
            </section>

            {/* First Purchase Products */}
            <section>
              <SectionTitle>Products Purchased</SectionTitle>
              <ProductCategoriesGrid>
                <div>
                  <SectionTitleWithInfo>
                    <h4>First Purchase - Your Brand</h4>
                  </SectionTitleWithInfo>
                  <CategoryList>
                    {persona.top_brand_first_products_purchased?.map(item => (
                      <li key={`${item.product}`}>
                        <span>{item.product}</span>
                        <span>{item.percentage}%</span>
                      </li>
                    ))}
                  </CategoryList>
                </div>
                <div>
                  <SectionTitleWithInfo>
                    <h4>Top Products - Other Brands</h4>
                  </SectionTitleWithInfo>
                  <CategoryList>
                    {persona?.top_other_brands_product_categories?.slice(0, 5).map(item => (
                      <li key={`${item.category}`}>
                        <span>{item.category}</span>
                        <span>{item.percentage}%</span>
                      </li>
                    ))}
                  </CategoryList>
                </div>
              </ProductCategoriesGrid>
            </section>

            {/* Creative Strategy Insights */}
            <section>
              <SectionTitle>
                Creative Strategy Insights
                <span
                  style={{
                    verticalAlign: 'middle',
                    display: 'inline-flex',
                    marginLeft: '8px',
                    alignItems: 'center'
                  }}
                >
                  <Tooltip
                    title={
                      <div style={{ padding: '8px 12px' }}>
                        <Text variant="body3">
                          These shopping patterns across their entire life can help inform
                          model/influencer selection, styling direction, and value proposition
                          framing.
                        </Text>
                      </div>
                    }
                    arrow
                    info
                  />
                </span>
              </SectionTitle>
              <ProductCategoriesGrid>
                {!!persona.specialized_tag_types &&
                  persona.specialized_tag_types.map(specializedTag => (
                    <div key={`${specializedTag?.type_display_name}`}>
                      <SectionTitleWithInfo>
                        <h4> {specializedTag?.type_display_name} </h4>
                        <Tooltip
                          title={
                            <div style={{ padding: '12px 7px', width: 'center' }}>
                              <Text variant="body3">{specializedTag?.type_helper_text}</Text>
                            </div>
                          }
                          placement="top"
                          arrow
                          info
                        />
                      </SectionTitleWithInfo>
                      <CategoryList>
                        {specializedTag?.individual_tags?.map(item => (
                          <li key={`${item.display_name}`}>
                            <span>{item.display_name}</span>
                            <span>{item.percentage_population}%</span>
                          </li>
                        ))}
                      </CategoryList>
                    </div>
                  ))}
              </ProductCategoriesGrid>
            </section>

            {/* Demographics */}
            <section>
              <DetailsGrid>
                <div>
                  <SectionTitle>Age Distribution</SectionTitle>
                  <DetailsList>
                    {['18-24', '25-34', '35-44', '45-54', '55-64', '65+'].map(age => {
                      const percentage = persona.age_distribution[age] || 0;
                      return (
                        <li key={`${age}`}>
                          <span className="label">{age}</span>
                          <div className="bar-container">
                            <div className="bar" style={{ width: `${percentage}%` }} />
                          </div>
                          <span className="value">{percentage}%</span>
                        </li>
                      );
                    })}
                  </DetailsList>
                </div>
                <div>
                  <SectionTitle>Gender Distribution</SectionTitle>
                  <DetailsList>
                    {['Female', 'Male', 'Unknown'].map(gender => {
                      const percentage = persona.gender_distribution[gender] || 0;
                      return (
                        <li key={`${gender}`}>
                          <span className="label">{gender}</span>
                          <div className="bar-container">
                            <div className="bar" style={{ width: `${percentage}%` }} />
                          </div>
                          <span className="value">{percentage}%</span>
                        </li>
                      );
                    })}
                  </DetailsList>
                </div>
              </DetailsGrid>
            </section>

            {/* Geography */}
            <section>
              <SectionTitle>Geography</SectionTitle>
              <GeoDistributionSummary>
                Suburban: {persona.residence_type_breakdown.SUBURBAN}% • Urban:{' '}
                {persona.residence_type_breakdown.METRO_SPRAWL}% • Metro Sprawl:{' '}
                {persona.residence_type_breakdown.URBAN}% • Rural:{' '}
                {persona.residence_type_breakdown.RURAL}%
              </GeoDistributionSummary>
              <GeoCommunityList>
                {Object.entries(persona.settlement_tag_breakdown || {})
                  .filter(([, locations]) => Array.isArray(locations) && locations.length > 0)
                  .slice(0, 3)
                  .map(([settlementType, locations]) => (
                    <GeoCommunityItem key={settlementType}>
                      <span className="icon">{getSettlementIcon(settlementType)}</span>
                      <div className="info">
                        <h4>{formatSettlementTitle(settlementType)}</h4>
                        <p>
                          {(locations as string[]).map(location => (
                            <LocationTag key={`${location}`}>{location}</LocationTag>
                          ))}
                        </p>
                      </div>
                    </GeoCommunityItem>
                  ))}
              </GeoCommunityList>
            </section>
          </PersonaCard>
        ))}
      </PersonasContainer>
    </Container>
  );
};

export default BrandPersonasView;
